import { c as _c } from "react/compiler-runtime";
import { IcoBolt24, IcoIdCard24, IcoShield24 } from '@onefootprint/icons';
import type { AdverseMediaListKind, GraphOperationUpdateNode, ModernVerificationCheckConfigBaseLayer, ModernVerificationCheckConfigBusinessAml, ModernVerificationCheckConfigFingerprint, ModernVerificationCheckConfigKyb, ModernVerificationCheckConfigKyc, ModernVerificationCheckConfigNeuroId, ModernVerificationCheckConfigSentilink, NodeConfigurationContextVerificationCheck, VerificationCheckDisabledReason } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Radio, Shimmer, Tooltip } from '@onefootprint/ui';
import isEqual from 'lodash/isEqual';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { VerificationCheckNodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import Section from '../../../section';
import UserAmlForm from '../../user-aml-form';
import type { VerificationCheckFormValues } from '../../verification-check.types';
import TooltipToggle from './components/tooltip-toggle';
type VerificationCheckFormProps = {
  node: VerificationCheckNodeProps;
  canEdit: boolean;
  config: NodeConfigurationContextVerificationCheck;
  mutation: ApplyEditMutation;
};

// A Record (not an array) so adding an AdverseMediaListKind variant fails to compile here until it's listed.
const ALL_ADVERSE_MEDIA_CATEGORIES: Record<AdverseMediaListKind, true> = {
  financial_crime: true,
  violent_crime: true,
  sexual_crime: true,
  cyber_crime: true,
  terrorism: true,
  fraud: true,
  narcotics: true,
  general_serious: true,
  general_minor: true
};
const ADVERSE_MEDIA_CATEGORIES = Object.keys(ALL_ADVERSE_MEDIA_CATEGORIES) as AdverseMediaListKind[];

// A null/omitted list means "all categories" server-side, so reflect that as all-selected.
const getAdverseMediaListDefaults = (adverseMedia: boolean, storedLists: AdverseMediaListKind[] | null | undefined): VerificationCheckFormValues['aml']['adverseMediaList'] => {
  const selected = adverseMedia && storedLists == null ? ADVERSE_MEDIA_CATEGORIES : storedLists ?? [];
  const entries = ADVERSE_MEDIA_CATEGORIES.map(category => [category, selected.includes(category)] as [AdverseMediaListKind, boolean]);
  return Object.fromEntries(entries) as VerificationCheckFormValues['aml']['adverseMediaList'];
};
export const VerificationCheckFormInternal = t0 => {
  const $ = _c(114);
  const {
    node,
    canEdit,
    config,
    mutation
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  let baselayerCheck;
  let kybCheck;
  let matchKind;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== node.data.verificationChecks) {
    const amlCheck = node.data.verificationChecks.find(_temp);
    kybCheck = node.data.verificationChecks.find(_temp2);
    baselayerCheck = node.data.verificationChecks.find(_temp3);
    const fingerprintCheck = node.data.verificationChecks.find(_temp4);
    matchKind = amlCheck?.data.matchKind || "fuzzy_low";
    t7 = node.data.verificationChecks.some(_temp5);
    t8 = node.data.verificationChecks.some(_temp6);
    t9 = node.data.verificationChecks.some(_temp7);
    t10 = !!fingerprintCheck;
    t11 = fingerprintCheck?.data.outputs.find(_temp8)?.onboardingDataKey ?? "device_insights";
    t1 = !!amlCheck;
    t2 = amlCheck?.data.ofac ?? false;
    t3 = amlCheck?.data.pep ?? false;
    t4 = amlCheck?.data.adverseMedia ?? false;
    t5 = amlCheck?.data.continuousMonitoring ?? true;
    t6 = getAdverseMediaListDefaults(amlCheck?.data.adverseMedia ?? false, amlCheck?.data.adverseMediaLists);
    $[0] = node.data.verificationChecks;
    $[1] = baselayerCheck;
    $[2] = kybCheck;
    $[3] = matchKind;
    $[4] = t1;
    $[5] = t10;
    $[6] = t11;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    baselayerCheck = $[1];
    kybCheck = $[2];
    matchKind = $[3];
    t1 = $[4];
    t10 = $[5];
    t11 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  const t12 = matchKind === "exact_name" || matchKind === "exact_name_and_dob_year" ? "exact" : "fuzzy";
  const t13 = matchKind !== "exact_name" && matchKind !== "exact_name_and_dob_year" ? matchKind : "fuzzy_low";
  const t14 = matchKind === "exact_name" || matchKind === "exact_name_and_dob_year" ? matchKind : "exact_name";
  let t15;
  if ($[15] !== t12 || $[16] !== t13 || $[17] !== t14) {
    t15 = {
      kind: t12,
      fuzzyLevel: t13,
      exactLevel: t14
    };
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== t1 || $[20] !== t15 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t16 = {
      enhancedAml: t1,
      ofac: t2,
      pep: t3,
      adverseMedia: t4,
      continuousMonitoring: t5,
      adverseMediaList: t6,
      matchingMethod: t15
    };
    $[19] = t1;
    $[20] = t15;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = !!kybCheck;
  const t18 = kybCheck?.data.einOnly ?? false;
  let t19;
  if ($[27] !== node.data.verificationChecks) {
    t19 = node.data.verificationChecks.some(_temp9);
    $[27] = node.data.verificationChecks;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = !!baselayerCheck;
  const t21 = baselayerCheck?.data.einOnly ?? false;
  let t22;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t16 || $[32] !== t17 || $[33] !== t18 || $[34] !== t19 || $[35] !== t20 || $[36] !== t21 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t22 = {
      kyc: t7,
      sentilink: t8,
      neuroId: t9,
      fingerprint: t10,
      fingerprintKey: t11,
      aml: t16,
      kyb: t17,
      einOnly: t18,
      businessAml: t19,
      baselayer: t20,
      baselayerEinOnly: t21
    };
    $[29] = t10;
    $[30] = t11;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const defaultValues = t22;
  const {
    toggle
  } = useDirty();
  let t23;
  if ($[41] !== defaultValues) {
    t23 = {
      defaultValues
    };
    $[41] = defaultValues;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const methods = useForm(t23);
  const {
    control,
    handleSubmit,
    formState
  } = methods;
  let t24;
  if ($[43] !== control) {
    t24 = {
      control,
      name: "aml"
    };
    $[43] = control;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const amlValues = useWatch(t24);
  let t25;
  if ($[45] !== amlValues || $[46] !== defaultValues.aml || $[47] !== formState.isDirty || $[48] !== toggle) {
    t25 = () => {
      const isAmlFormDirty = !isEqual(amlValues, defaultValues.aml);
      toggle("verification-check-form", Boolean(formState.isDirty) || isAmlFormDirty);
    };
    $[45] = amlValues;
    $[46] = defaultValues.aml;
    $[47] = formState.isDirty;
    $[48] = toggle;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== amlValues || $[51] !== formState.isDirty) {
    t26 = [formState.isDirty, amlValues];
    $[50] = amlValues;
    $[51] = formState.isDirty;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  useEffect(t25, t26);
  let t27;
  if ($[53] !== mutation || $[54] !== node.id) {
    t27 = async values => {
      const verificationChecks = [];
      if (values.kyc) {
        verificationChecks.push({
          kind: "kyc" as const,
          data: {}
        });
      }
      if (values.sentilink) {
        verificationChecks.push({
          kind: "sentilink" as const,
          data: {}
        });
      }
      if (values.neuroId) {
        verificationChecks.push({
          kind: "neuro_id" as const,
          data: {}
        });
      }
      if (values.fingerprint) {
        verificationChecks.push({
          kind: "fingerprint" as const,
          data: {
            outputs: [{
              kind: "device_insights" as const,
              onboardingDataKey: values.fingerprintKey
            }]
          }
        });
      }
      if (values.aml.enhancedAml) {
        const adverseMediaLists = Object.entries(values.aml.adverseMediaList).filter(_temp10).map(_temp11);
        const matchKind_0 = values.aml.matchingMethod.kind === "fuzzy" ? values.aml.matchingMethod.fuzzyLevel : values.aml.matchingMethod.exactLevel;
        verificationChecks.push({
          kind: "aml" as const,
          data: {
            ofac: values.aml.ofac,
            pep: values.aml.pep,
            adverseMedia: values.aml.adverseMedia,
            adverseMediaLists: values.aml.adverseMedia ? adverseMediaLists : [],
            matchKind: matchKind_0,
            continuousMonitoring: values.aml.continuousMonitoring
          }
        });
      }
      if (values.kyb) {
        verificationChecks.push({
          kind: "kyb" as const,
          data: {
            einOnly: values.einOnly
          }
        });
      }
      if (values.businessAml) {
        verificationChecks.push({
          kind: "business_aml" as const,
          data: {}
        });
      }
      if (values.baselayer) {
        verificationChecks.push({
          kind: "base_layer" as const,
          data: {
            einOnly: values.baselayerEinOnly
          }
        });
      }
      ;
      try {
        const operation = {
          kind: "update_node",
          nodeData: {
            kind: "verification_check",
            verificationChecks
          },
          nodeId: node.id
        };
        await mutation.mutateAsync(operation);
      } catch (t28) {
        const error = t28;
        console.error("Error updating playbook session:", error);
      }
    };
    $[53] = mutation;
    $[54] = node.id;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const handleFormSubmit = t27;
  let t28;
  if ($[56] !== config.verificationChecks) {
    t28 = config.verificationChecks.find(_temp12);
    $[56] = config.verificationChecks;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const kyb = t28;
  let t29;
  if ($[58] !== config.verificationChecks) {
    t29 = config.verificationChecks.find(_temp13);
    $[58] = config.verificationChecks;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const kyc = t29;
  let t30;
  if ($[60] !== config.verificationChecks) {
    t30 = config.verificationChecks.find(_temp14);
    $[60] = config.verificationChecks;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  const aml = t30;
  let t31;
  if ($[62] !== config.verificationChecks) {
    t31 = config.verificationChecks.find(_temp15);
    $[62] = config.verificationChecks;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  const businessAml = t31;
  let t32;
  if ($[64] !== config.verificationChecks) {
    t32 = config.verificationChecks.find(_temp16);
    $[64] = config.verificationChecks;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const neuro = t32;
  let t33;
  if ($[66] !== config.verificationChecks) {
    t33 = config.verificationChecks.find(_temp17);
    $[66] = config.verificationChecks;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  const sentilink = t33;
  let t34;
  if ($[68] !== config.verificationChecks) {
    t34 = config.verificationChecks.find(_temp18);
    $[68] = config.verificationChecks;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  const fingerprint = t34;
  let t35;
  if ($[70] !== config.verificationChecks) {
    t35 = config.verificationChecks.find(_temp19);
    $[70] = config.verificationChecks;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  const baselayer = t35;
  let t36;
  if ($[72] !== handleFormSubmit || $[73] !== handleSubmit) {
    t36 = handleSubmit(handleFormSubmit);
    $[72] = handleFormSubmit;
    $[73] = handleSubmit;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  let t37;
  if ($[75] !== canEdit || $[76] !== kyc || $[77] !== t) {
    t37 = !!kyc && <Section title={t(_temp20)} Icon={IcoIdCard24} testID="kyc-section"><KycToggle config={kyc} canEdit={canEdit} /></Section>;
    $[75] = canEdit;
    $[76] = kyc;
    $[77] = t;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== aml || $[80] !== canEdit || $[81] !== t) {
    t38 = !!aml && <Section title={t(_temp21)} Icon={IcoShield24} testID="aml-section"><UserAmlForm disabled={!canEdit} /></Section>;
    $[79] = aml;
    $[80] = canEdit;
    $[81] = t;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== canEdit || $[84] !== fingerprint || $[85] !== neuro || $[86] !== sentilink || $[87] !== t) {
    t39 = (!!sentilink || !!neuro || !!fingerprint) && <Section title={t(_temp22)} Icon={IcoBolt24} testID="fraud-section">{!!sentilink && <SentilinkToggle config={sentilink} canEdit={canEdit} />}{!!neuro && <NeuroToggle config={neuro} canEdit={canEdit} />}{!!fingerprint && <FingerprintToggle config={fingerprint} canEdit={canEdit} />}</Section>;
    $[83] = canEdit;
    $[84] = fingerprint;
    $[85] = neuro;
    $[86] = sentilink;
    $[87] = t;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== canEdit || $[90] !== kyb || $[91] !== t) {
    t40 = !!kyb && <Section title={t(_temp23)} Icon={IcoIdCard24} testID="kyb-section"><KybToggle config={kyb} canEdit={canEdit} /></Section>;
    $[89] = canEdit;
    $[90] = kyb;
    $[91] = t;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== baselayer || $[94] !== canEdit || $[95] !== t) {
    t41 = !!baselayer && <Section title={t(_temp24)} Icon={IcoIdCard24} testID="base-layer-section"><BaseLayerToggle config={baselayer} canEdit={canEdit} /></Section>;
    $[93] = baselayer;
    $[94] = canEdit;
    $[95] = t;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== businessAml || $[98] !== canEdit || $[99] !== t) {
    t42 = !!businessAml && <Section title={t(_temp25)} Icon={IcoShield24} testID="business-aml-section"><BusinessAmlToggle config={businessAml} canEdit={canEdit} /></Section>;
    $[97] = businessAml;
    $[98] = canEdit;
    $[99] = t;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== t37 || $[102] !== t38 || $[103] !== t39 || $[104] !== t40 || $[105] !== t41 || $[106] !== t42) {
    t43 = <div className="flex flex-col gap-6"><div className="flex flex-col gap-4 mb-8">{t37}{t38}{t39}{t40}{t41}{t42}</div></div>;
    $[101] = t37;
    $[102] = t38;
    $[103] = t39;
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] !== t36 || $[109] !== t43) {
    t44 = <form id="verification-check-form" onSubmit={t36}>{t43}</form>;
    $[108] = t36;
    $[109] = t43;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  let t45;
  if ($[111] !== methods || $[112] !== t44) {
    t45 = <FormProvider {...methods}>{t44}</FormProvider>;
    $[111] = methods;
    $[112] = t44;
    $[113] = t45;
  } else {
    t45 = $[113];
  }
  return t45;
};
const useGetDisabledReasonText = reason => {
  const $ = _c(19);
  const {
    t
  } = useTranslation("playbook-builder");
  if (!reason) {
    return;
  }
  if (reason.kind === "missing_required_data") {
    let t0;
    if ($[0] !== reason.description || $[1] !== t) {
      t0 = t(_temp26, {
        data: reason.description
      });
      $[0] = reason.description;
      $[1] = t;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== t) {
    t0 = t(_temp27);
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== t) {
    t1 = t(_temp28);
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t) {
    t2 = t(_temp29);
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t) {
    t3 = t(_temp30);
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t(_temp31);
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      cannot_disable_bo_kyc_in_kyb: t0,
      unsupported_graph_subject: t1,
      not_enabled_for_tenant: t2,
      internationl_residents_not_supported: t3,
      must_run_full_kyb: t4
    };
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const disabledReasonText = t5;
  return disabledReasonText[reason.kind];
};
const KycToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp32);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp33);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="kyc" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const SentilinkToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp34);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp35);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="sentilink" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const NeuroToggle = t0 => {
  const $ = _c(9);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp36);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp37);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="neuroId" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const FingerprintToggle = t0 => {
  const $ = _c(18);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control,
      name: "fingerprint"
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const runFingerprint = useWatch(t1);
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp38);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t(_temp39);
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== canEdit || $[7] !== t2 || $[8] !== t3 || $[9] !== tooltipText) {
    t4 = <TooltipToggle name="fingerprint" canEdit={canEdit} label={t2} hint={t3} disabledTooltipText={tooltipText} />;
    $[6] = canEdit;
    $[7] = t2;
    $[8] = t3;
    $[9] = tooltipText;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== canEdit || $[12] !== runFingerprint || $[13] !== t) {
    t5 = runFingerprint && <Form.Input name="fingerprintKey" label={t(_temp40)} placeholder={t(_temp41)} addon="data." disabled={!canEdit} size="compact" rules={{
      required: t(_temp42)
    }} />;
    $[11] = canEdit;
    $[12] = runFingerprint;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const KybToggle = t0 => {
  const $ = _c(21);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["kyb"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [runKyb] = useWatch(t2);
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp43);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp44);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== canEdit || $[8] !== t3 || $[9] !== t4 || $[10] !== tooltipText) {
    t5 = <TooltipToggle name="kyb" canEdit={canEdit} label={t3} hint={t4} disabledTooltipText={tooltipText} />;
    $[7] = canEdit;
    $[8] = t3;
    $[9] = t4;
    $[10] = tooltipText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== canEdit || $[13] !== config.canRunFullKyb || $[14] !== control || $[15] !== runKyb || $[16] !== t) {
    t6 = runKyb ? <><Divider variant="secondary" /><Controller name="einOnly" control={control} render={t7 => {
        const {
          field: t8
        } = t7;
        const {
          value,
          onChange,
          onBlur
        } = t8;
        return <div className="flex flex-col gap-3"><Tooltip disabled={config.canRunFullKyb} content={t(_temp45)}><Radio label={t(_temp46)} value="full" checked={!value} onChange={() => onChange(false)} onBlur={onBlur} disabled={!canEdit || !config.canRunFullKyb} /></Tooltip><Radio hint={t(_temp47)} label={t(_temp48)} value="ein" checked={value} onChange={() => onChange(true)} onBlur={onBlur} disabled={!canEdit} /></div>;
      }} /></> : null;
    $[12] = canEdit;
    $[13] = config.canRunFullKyb;
    $[14] = control;
    $[15] = runKyb;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
const BusinessAmlToggle = t0 => {
  const $ = _c(9);
  const {
    canEdit,
    config
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp49);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t(_temp50);
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== canEdit || $[5] !== t1 || $[6] !== t2 || $[7] !== tooltipText) {
    t3 = <TooltipToggle name="businessAml" canEdit={canEdit} label={t1} hint={t2} disabledTooltipText={tooltipText} />;
    $[4] = canEdit;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const BaseLayerToggle = t0 => {
  const $ = _c(21);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["baselayer"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [runBaseLayer] = useWatch(t2);
  const tooltipText = useGetDisabledReasonText(config.disabledReason);
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp51);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp52);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== canEdit || $[8] !== t3 || $[9] !== t4 || $[10] !== tooltipText) {
    t5 = <TooltipToggle name="baselayer" canEdit={canEdit} label={t3} hint={t4} disabledTooltipText={tooltipText} />;
    $[7] = canEdit;
    $[8] = t3;
    $[9] = t4;
    $[10] = tooltipText;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== canEdit || $[13] !== config.canRunFullKyb || $[14] !== control || $[15] !== runBaseLayer || $[16] !== t) {
    t6 = runBaseLayer ? <><Divider variant="secondary" /><Controller name="baselayerEinOnly" control={control} render={t7 => {
        const {
          field: t8
        } = t7;
        const {
          value,
          onChange,
          onBlur
        } = t8;
        return <div className="flex flex-col gap-3"><Tooltip disabled={config.canRunFullKyb} content={t(_temp53)}><Radio label={t(_temp54)} value="full" checked={!value} onChange={() => onChange(false)} onBlur={onBlur} disabled={!canEdit || !config.canRunFullKyb} /></Tooltip><Radio hint={t(_temp55)} label={t(_temp56)} value="ein" checked={value} onChange={() => onChange(true)} onBlur={onBlur} disabled={!canEdit} /></div>;
      }} /></> : null;
    $[12] = canEdit;
    $[13] = config.canRunFullKyb;
    $[14] = control;
    $[15] = runBaseLayer;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
export default ({
  node,
  canEdit,
  mutation
}: Omit<VerificationCheckFormProps, 'config'>) => {
  const config = useGetNodeConfigContext(node.id, 'verification_check');
  if (!config) {
    return <div className="flex flex-col gap-4">
        <Shimmer width="100%" height="103px" />
        <Shimmer width="100%" height="103px" />
      </div>;
  }
  return <VerificationCheckFormInternal node={node} canEdit={canEdit} config={config} mutation={mutation} />;
};
function _temp(check) {
  return check.kind === "aml";
}
function _temp2(check_0) {
  return check_0.kind === "kyb";
}
function _temp3(check_1) {
  return check_1.kind === "base_layer";
}
function _temp4(check_2) {
  return check_2.kind === "fingerprint";
}
function _temp5(check_3) {
  return check_3.kind === "kyc";
}
function _temp6(check_4) {
  return check_4.kind === "sentilink";
}
function _temp7(check_5) {
  return check_5.kind === "neuro_id";
}
function _temp8(output) {
  return output.kind === "device_insights";
}
function _temp9(check_6) {
  return check_6.kind === "business_aml";
}
function _temp10(t0) {
  const [, enabled] = t0;
  return enabled;
}
function _temp11(t0) {
  const [listName] = t0;
  return listName as AdverseMediaListKind;
}
function _temp12(check_7) {
  return check_7.kind === "kyb";
}
function _temp13(check_8) {
  return check_8.kind === "kyc";
}
function _temp14(check_9) {
  return check_9.kind === "aml";
}
function _temp15(check_10) {
  return check_10.kind === "business_aml";
}
function _temp16(check_11) {
  return check_11.kind === "neuro_id";
}
function _temp17(check_12) {
  return check_12.kind === "sentilink";
}
function _temp18(check_13) {
  return check_13.kind === "fingerprint";
}
function _temp19(check_14) {
  return check_14.kind === "base_layer";
}
function _temp20($) {
  return $.verificationChecks.kyc.title;
}
function _temp21($_0) {
  return $_0.verificationChecks.aml.title;
}
function _temp22($_1) {
  return $_1.verificationChecks.fraud.title;
}
function _temp23($_2) {
  return $_2.verificationChecks.kyb.run.title;
}
function _temp24($_3) {
  return $_3.verificationChecks.baselayer.run.title;
}
function _temp25($_4) {
  return $_4.verificationChecks.businessAml.title;
}
function _temp26($) {
  return $.verificationChecks.general.missingRequiredData;
}
function _temp27($_0) {
  return $_0.verificationChecks.kyc.disabledInBoSubgraph;
}
function _temp28($_1) {
  return $_1.verificationChecks.general.unsupportedGraphSubject;
}
function _temp29($_2) {
  return $_2.verificationChecks.general.notEnabledForTenant;
}
function _temp30($_3) {
  return $_3.verificationChecks.general.internationalCountriesUnsupported;
}
function _temp31($_4) {
  return $_4.verificationChecks.disabled.mustRunFullKyb;
}
function _temp32($) {
  return $.verificationChecks.kyc.label;
}
function _temp33($_0) {
  return $_0.verificationChecks.kyc.hint;
}
function _temp34($) {
  return $.verificationChecks.fraud.sentilink.label;
}
function _temp35($_0) {
  return $_0.verificationChecks.fraud.sentilink.hint;
}
function _temp36($) {
  return $.verificationChecks.fraud.neuroid.label;
}
function _temp37($_0) {
  return $_0.verificationChecks.fraud.neuroid.hint;
}
function _temp38($) {
  return $.verificationChecks.fraud.fingerprint.label;
}
function _temp39($_0) {
  return $_0.verificationChecks.fraud.fingerprint.hint;
}
function _temp40($_1) {
  return $_1.verificationChecks.fraud.fingerprint.dataKeyLabel;
}
function _temp41($_2) {
  return $_2.nodeOutputs.dataKey.placeholder;
}
function _temp42($_3) {
  return $_3.nodeOutputs.dataKey.required;
}
function _temp43($) {
  return $.verificationChecks.kyb.run.label;
}
function _temp44($_0) {
  return $_0.verificationChecks.kyb.run.hint;
}
function _temp45($_1) {
  return $_1.verificationChecks.kyb.full.disabled;
}
function _temp46($_2) {
  return $_2.verificationChecks.kyb.full.label;
}
function _temp47($_3) {
  return $_3.verificationChecks.kyb.ein.hint;
}
function _temp48($_4) {
  return $_4.verificationChecks.kyb.ein.label;
}
function _temp49($) {
  return $.verificationChecks.businessAml.label;
}
function _temp50($_0) {
  return $_0.verificationChecks.businessAml.hint;
}
function _temp51($) {
  return $.verificationChecks.baselayer.run.label;
}
function _temp52($_0) {
  return $_0.verificationChecks.baselayer.run.hint;
}
function _temp53($_1) {
  return $_1.verificationChecks.baselayer.full.disabled;
}
function _temp54($_2) {
  return $_2.verificationChecks.baselayer.full.label;
}
function _temp55($_3) {
  return $_3.verificationChecks.baselayer.ein.hint;
}
function _temp56($_4) {
  return $_4.verificationChecks.baselayer.ein.label;
}